import React, { useState } from 'react';
import { Paper, Typography, Button, Stack } from '@mui/material';

import { useAuth } from '../../../hooks';
import { isPatient } from '../../../utils';
import FileUploader from '../file-uploader/file-uploader-container';

/**
 * Component for manual verification of identity documents.
 *
 * @param {Object} props - The component props.
 * @param {Function} props.handleNext - Function to handle the next step after submission.
 * @param {string} props.frontUrl - URL of the uploaded front document.
 * @param {Function} props.setFrontUrl - Function to update the front document URL.
 * @param {string} props.backUrl - URL of the uploaded back document.
 * @param {Function} props.setBackUrl - Function to update the back document URL.
 * @param {string} props.patientId - The patient’s ID.
 * @param {boolean} [props.isHidden=false] - Flag to determine if the component should be hidden.
 *
 * @returns {JSX.Element|null} The rendered component or null if `isHidden` is `true`.
 */
export const ManualVerificationPresentation = ({
  handleNext,
  frontUrl,
  setFrontUrl,
  backUrl,
  setBackUrl,
  patientId,
  isHidden = false,
}) => {
  const { userType } = useAuth();
  const [isButtonLoading, setIsButtonLoading] = useState(false);

  if (isHidden) {
    return null;
  }

  const noUrlLinks = !frontUrl || !backUrl;

  const frontObj = {
    buttonText: 'Upload Front',
    storagePath: `patient_documents/${patientId}/identity/front`,
    reference: 'front',
    fileType: 'image/*',
  };

  const backObj = {
    buttonText: 'Upload Back',
    storagePath: `patient_documents/${patientId}/identity/back`,
    reference: 'back',
    fileType: 'image/*',
  };

  const handleSubmit = async () => {
    setIsButtonLoading(true);
    await handleNext();
    setIsButtonLoading(false);
  };

  return (
    <Stack component={Paper} maxWidth="sm" direction="column" spacing={4} padding={4}>
      <Typography variant="h5" align="center">
        Manual Verification
      </Typography>
      <Typography variant="body1" align="center" whiteSpace="pre-line" gutterBottom>
        Choose a front and back photo of {isPatient(userType) ? 'your' : `the patient's`} drivers license or 18+ card,
        then click the submit button below.
      </Typography>
      <FileUploader url={frontUrl} setUrl={setFrontUrl} currentFormObject={frontObj} formContext />
      <FileUploader url={backUrl} setUrl={setBackUrl} currentFormObject={backObj} formContext />
      <Button
        color="primary"
        variant="contained"
        disabled={noUrlLinks}
        onClick={handleSubmit}
        loading={isButtonLoading}
      >
        Submit For Verification
      </Button>
    </Stack>
  );
};
